import { formatCurrency } from '../../utils/formatters';

// A multi-day recurring expense (e.g. paid on the 1st and the 15th)
// projects one row per occurrence into the browsed month, all sharing the
// same id — roll those back up into a single line so the annual total
// isn't split across duplicate-looking rows.
const groupRecurring = (expenses) => {
  const byId = new Map();
  for (const e of expenses.filter(e => e.recurring)) {
    const existing = byId.get(e.id);
    if (existing) {
      existing.monthlyAmount += e.amount;
      existing.occurrences += 1;
    } else {
      byId.set(e.id, { id: e.id, title: e.title, category: e.category, monthlyAmount: e.amount, occurrences: 1 });
    }
  }
  return [...byId.values()].sort((a, b) => b.monthlyAmount - a.monthlyAmount);
};

const AnnualView = ({ expenses, loading }) => {
  const recurring = groupRecurring(expenses);

  const monthlyTotal = recurring.reduce((s, r) => s + r.monthlyAmount, 0);
  const annualTotal = monthlyTotal * 12;

  return (
    <div className="space-y-4">
      <h2 className="text-gray-900 font-semibold text-lg tracking-tight">Annual</h2>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">Loading…</div>
        ) : recurring.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">No recurring expenses</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recurring.map(r => (
              <div key={r.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {r.title}
                    {r.occurrences > 1 && <span className="text-gray-400"> ×{r.occurrences}/mo</span>}
                  </span>
                  {r.category && (
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                      {r.category}
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-end flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-900 tabular-nums">
                    -{formatCurrency(r.monthlyAmount * 12)}
                  </span>
                  <span className="text-xs text-gray-400 tabular-nums">{formatCurrency(r.monthlyAmount)}/mo</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-card divide-y divide-gray-100 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3">
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Monthly</span>
          <span className="text-sm font-semibold text-gray-900 tabular-nums">-{formatCurrency(monthlyTotal)}</span>
        </div>
        <div className="flex items-center justify-between px-5 py-3 bg-gray-50">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Annual</span>
          <span className="text-sm font-semibold text-gray-900 tabular-nums">-{formatCurrency(annualTotal)}</span>
        </div>
      </div>
    </div>
  );
};

export default AnnualView;
